import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { ApiRequestError } from '@/lib/api';
import { useAuth } from './useAuth';

/** Single-operator login (SPEC §10), built on the design system (M0-06). */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    setSubmitting(true);
    try {
      await login(password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.code === 'validation_error')
          setFieldError(err.fieldErrors?.password?.[0] ?? 'Password is required');
        else if (err.code === 'rate_limited')
          setError('Too many attempts. Please wait a moment and try again.');
        else setError('Invalid credentials.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-xl font-semibold leading-none tracking-tight">Outreach Hub</h1>
          <CardDescription>Sign in to continue.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            {/* Hidden username helps password managers / accessibility. */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              defaultValue="operator"
              readOnly
              hidden
              aria-hidden
            />
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? 'password-error' : undefined}
              />
              {fieldError && (
                <p id="password-error" role="alert" className="text-sm text-destructive">
                  {fieldError}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
